import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { 
  Users, Trophy, Flame, Target, Calendar as CalendarIcon, 
  Crown, Copy, ArrowLeft, Plus, Minus, Share2, LogOut,
  Medal, Zap, Clock, CheckCircle2, UserMinus
} from 'lucide-react';
import { PandaLevelBadge } from '../components/MonkLevel';
import { cn } from '../lib/utils';

export default function SharedGoalDetail() {
  const { goalId } = useParams();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  
  const [goal, setGoal] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [progressInput, setProgressInput] = useState(1);

  useEffect(() => {
    // Any participant can view their shared goals (not just Pro users)
    fetchGoalData();
  }, [goalId, user]);

  const fetchGoalData = async () => {
    try {
      const [goalRes, leaderboardRes] = await Promise.all([
        api.get(`/api/shared-goals/${goalId}`),
        api.get(`/api/shared-goals/${goalId}/leaderboard`)
      ]);
      setGoal(goalRes.data);
      setLeaderboard(leaderboardRes.data);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Shared goal not found');
        navigate('/shared-goals');
      } else if (err.response?.status === 403) {
        toast.error('You are not a participant of this goal');
        navigate('/shared-goals');
      } else {
        toast.error('Failed to load goal');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProgress = async (delta = 1) => {
    if (goal.status !== 'ACTIVE') {
      toast.error('This goal is no longer active');
      return;
    }
    
    setUpdating(true);
    try {
      const res = await api.put(`/api/shared-goals/${goalId}/progress`, {
        progress_delta: delta
      });
      
      toast.success(`Progress updated! ${res.data.current_progress}/${res.data.target_value}`);
      
      if (res.data.status === 'COMPLETED') {
        toast.success('Congratulations! You completed your goal!', {
          icon: '🎉'
        });
      }
      
      fetchGoalData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update progress');
    } finally {
      setUpdating(false);
    }
  };

  const handleLeaveGoal = async () => {
    try {
      await api.delete(`/api/shared-goals/${goalId}`);
      toast.success(goal.is_creator ? 'Goal cancelled' : 'You left the goal');
      navigate('/shared-goals');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to leave goal');
    }
  };

  const handleRemoveUser = async () => {
    if (!userToRemove) return;
    
    try {
      await api.delete(`/api/shared-goals/${goalId}/participants/${userToRemove.user_id}`);
      toast.success(`${userToRemove.user_name} has been removed from the goal`);
      setShowRemoveDialog(false);
      setUserToRemove(null);
      fetchGoalData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove user');
    }
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}/join/${goal.invite_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied!');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-500/10 text-green-600 border-green-500/20';
      case 'COMPLETED': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'EXPIRED': return 'bg-coral/10 text-coral border-coral/20';
      case 'FAILED': return 'bg-red-500/10 text-red-600 border-red-500/20';
      default: return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-coral" />;
    return <span className="w-5 h-5 text-center text-muted-foreground font-medium">#{rank}</span>;
  };

  const getMilestoneProgress = (percentage) => {
    const milestones = [25, 50, 75, 100];
    return milestones.map(m => ({
      value: m,
      reached: percentage >= m
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!goal) {
    return null;
  }

  const userParticipation = goal.user_participation;
  const userProgress = userParticipation?.current_progress || 0;
  const userCompletionPct = Math.min(100, (userProgress / goal.target_value) * 100);
  const daysLeft = Math.max(0, Math.ceil((new Date(goal.deadline) - new Date()) / (1000 * 60 * 60 * 24)));

  return (
    <div className={cn(
      "min-h-screen",
      isDark ? "bg-[#050505]" : "bg-[#FAF9F5]"
    )}>
      <Navbar />
      
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className={cn("mb-6", isDark ? "" : "text-[#5F5F5F] hover:text-[#1A1A1A]")}
          onClick={() => navigate('/shared-goals')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Shared Goals
        </Button>

        {/* Header Card */}
        <Card className={cn(
          "mb-6 overflow-hidden",
          isDark ? "glass-card" : "bg-white border-[#EEEAE0] shadow-sm"
        )}>
          <div className={cn(
            "p-6",
            isDark ? "bg-gradient-to-r from-primary/20 via-primary/10 to-transparent" : "bg-gradient-to-r from-[#F4F3EE] via-[#FDF8F2] to-transparent"
          )}>
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h1 className={cn("text-2xl sm:text-3xl font-bold", isDark ? "text-white" : "text-[#1A1A1A]")}>
                    {goal.title}
                  </h1>
                  {goal.is_creator && (
                    <Crown className="w-6 h-6 text-yellow-500" title="You created this" />
                  )}
                </div>
                <p className={isDark ? "text-zinc-400" : "text-[#5F5F5F]"}>
                  {goal.description || `Target: ${goal.target_value} ${goal.target_unit}`}
                </p>
                <div className="flex items-center gap-4 mt-3">
                  <Badge className={getStatusColor(goal.status)}>
                    {goal.status}
                  </Badge>
                  <span className={cn("text-sm flex items-center gap-1", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>
                    <Users className="w-4 h-4" />
                    {goal.participant_count} participants
                  </span>
                  <span className={cn("text-sm flex items-center gap-1", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>
                    <Clock className="w-4 h-4" />
                    {daysLeft > 0 ? `${daysLeft} days left` : 'Ended'}
                  </span>
                </div>
              </div>
              
              <div className="flex gap-2">
                {goal.status === 'ACTIVE' && (
                  <Button variant="outline" onClick={() => setShowShareModal(true)} className={cn(isDark ? "" : "border-violet-300 text-slate-900 bg-white hover:bg-[#F4F3EE]")}>
                    <Share2 className="w-4 h-4 mr-2" />
                    Invite
                  </Button>
                )}
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => setShowLeaveDialog(true)}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {goal.is_creator ? 'Cancel Goal' : 'Leave'}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left Column - Progress & Actions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Your Progress Card */}
            <Card data-testid="user-progress-card" className={isDark ? "glass-card" : "bg-white border-[#EEEAE0] shadow-sm"}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-[#1A1A1A]")}>
                  <Target className="w-5 h-5 text-[#5F5F5F]" />
                  Your Progress
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Progress Bar with Milestones */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className={cn("font-medium", isDark ? "text-white" : "text-[#1A1A1A]")}>
                      {userProgress} / {goal.target_value} {goal.target_unit}
                    </span>
                    <span className={isDark ? "text-zinc-400" : "text-[#5F5F5F]"}>
                      {Math.round(userCompletionPct)}%
                    </span>
                  </div>
                  <div className="relative">
                    <Progress value={userCompletionPct} className="h-4" />
                    {/* Milestone Markers */}
                    <div className="absolute inset-0 flex justify-between px-0">
                      {getMilestoneProgress(userCompletionPct).map(({ value, reached }) => (
                        <div
                          key={value}
                          className={cn(
                            "w-4 h-4 rounded-full border-2 -mt-0 transform translate-y-0",
                            reached 
                              ? 'bg-[#F4F3EE]0 border-violet-500' 
                              : isDark ? 'bg-zinc-800 border-zinc-600' : 'bg-slate-100 border-slate-300'
                          )}
                          style={{ marginLeft: value === 25 ? '23%' : value === 50 ? '23%' : value === 75 ? '23%' : '-2%' }}
                          title={`${value}%`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className={cn("flex justify-between text-xs mt-1", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>100%</span>
                  </div>
                </div>

                {/* Streak Display */}
                {userParticipation?.streak_count > 0 && (
                  <div className={cn(
                    "flex items-center gap-2 p-3 rounded-lg",
                    isDark ? "bg-coral/10" : "bg-orange-50 border border-orange-200"
                  )}>
                    <Flame className="w-6 h-6 text-coral" />
                    <div>
                      <p className="font-semibold text-coral">
                        {userParticipation.streak_count} Day Streak!
                      </p>
                      <p className={cn("text-sm", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>
                        Keep it going!
                      </p>
                    </div>
                  </div>
                )}

                {/* Progress Actions */}
                {goal.status === 'ACTIVE' && userParticipation?.status === 'ACTIVE' && (
                  <div className={cn(
                    "flex flex-col sm:flex-row items-center gap-4 p-4 rounded-lg",
                    isDark ? "bg-zinc-800/50" : "bg-[#F4F3EE]/50 border border-[#EEEAE0]"
                  )}>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setProgressInput(Math.max(1, progressInput - 1))}
                        disabled={progressInput <= 1}
                        className={cn(isDark ? "" : "border-[#E5E2DA] text-[#1A1A1A] hover:text-slate-900")}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <Input
                        type="number"
                        min="1"
                        value={progressInput}
                        onChange={(e) => setProgressInput(Math.max(1, parseInt(e.target.value) || 1))}
                        className={cn("w-20 text-center", isDark ? "" : "border-[#E5E2DA] text-[#1A1A1A] bg-white")}
                        data-testid="progress-input"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setProgressInput(progressInput + 1)}
                        className={cn(isDark ? "" : "border-[#E5E2DA] text-[#1A1A1A] hover:text-slate-900")}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <Button 
                      onClick={() => handleUpdateProgress(progressInput)}
                      disabled={updating}
                      className="flex-1 sm:flex-none"
                      data-testid="log-progress-btn"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      {updating ? 'Logging...' : `Log ${progressInput} ${goal.target_unit}`}
                    </Button>
                  </div>
                )}

                {/* Completed Status */}
                {userParticipation?.status === 'COMPLETED' && (
                  <div className={cn(
                    "flex items-center gap-2 p-4 rounded-lg",
                    isDark ? "bg-green-500/10" : "bg-green-50 border border-green-200"
                  )}>
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                    <div>
                      <p className="font-semibold text-green-600">Goal Completed!</p>
                      <p className={cn("text-sm", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>
                        You&apos;ve reached your target. Amazing work!
                      </p>
                    </div>
                  </div>
                )}

                {/* Pledge Info */}
                {userParticipation?.pledge_enabled && (
                  <div className={cn("p-3 border rounded-lg", isDark ? "border-zinc-700" : "border-[#E5E2DA]")}>
                    <p className={cn("text-sm", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>Your Stake</p>
                    <p className={cn("font-semibold", isDark ? "text-white" : "text-[#1A1A1A]")}>
                      ${userParticipation.pledge_amount}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Leaderboard Card */}
            <Card data-testid="leaderboard-card" className={isDark ? "glass-card" : "bg-white border-[#EEEAE0] shadow-sm"}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-[#1A1A1A]")}>
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Leaderboard
                  {goal.is_creator && (
                    <Badge variant="outline" className={cn("ml-2 text-xs", isDark ? "" : "border-[#E5E2DA]")}>
                      <Crown className="w-3 h-3 mr-1" /> Admin
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className={isDark ? "text-zinc-400" : "text-[#5F5F5F]"}>
                  {leaderboard?.total_participants} participants competing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {leaderboard?.leaderboard.map((entry) => (
                    <div
                      key={entry.user_id}
                      className={cn(
                        "flex items-center gap-4 p-3 rounded-lg transition-colors",
                        entry.is_current_user 
                          ? isDark ? 'bg-[#F4F3EE]0/10 border border-violet-500/20' : 'bg-[#F4F3EE] border border-[#E5E2DA]'
                          : isDark ? 'bg-zinc-800/30 hover:bg-zinc-800/50' : 'bg-slate-50 hover:bg-slate-100'
                      )}
                      data-testid={`leaderboard-entry-${entry.rank}`}
                    >
                      {/* Rank */}
                      <div className="flex-shrink-0 w-8">
                        {getRankIcon(entry.rank)}
                      </div>

                      {/* User Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={cn(
                            "font-medium truncate",
                            entry.is_current_user ? 'text-[#5F5F5F]' : isDark ? 'text-white' : 'text-[#1A1A1A]'
                          )}>
                            {entry.user_name}
                            {entry.is_current_user && ' (You)'}
                          </p>
                          {/* Level Badge */}
                          <PandaLevelBadge level={entry.level || 1} size="xs" />
                          {entry.status === 'COMPLETED' && (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span>{entry.current_progress}/{entry.target_value}</span>
                          {entry.streak_count > 0 && (
                            <span className="flex items-center gap-1 text-coral">
                              <Flame className="w-3 h-3" />
                              {entry.streak_count}
                            </span>
                          )}
                          {entry.pledge_enabled && (
                            <span className={cn(
                              "text-xs px-2 py-0.5 rounded",
                              isDark ? "bg-zinc-800" : "bg-slate-100"
                            )}>
                              ${entry.pledge_amount} pledged
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Progress */}
                      <div className="flex-shrink-0 w-24">
                        <Progress value={entry.completion_percentage} className="h-2" />
                        <p className={cn("text-xs text-right mt-1", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
                          {entry.completion_percentage}%
                        </p>
                      </div>

                      {/* Admin: Remove User Button */}
                      {goal.is_creator && !entry.is_current_user && goal.status === 'ACTIVE' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn("flex-shrink-0 hover:text-destructive", isDark ? "text-zinc-500" : "text-[#9E8778]")}
                          onClick={() => {
                            setUserToRemove(entry);
                            setShowRemoveDialog(true);
                          }}
                          data-testid={`remove-user-${entry.user_id}`}
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Stats & Info */}
          <div className="space-y-6">
            {/* Goal Stats */}
            <Card className={isDark ? "glass-card" : "bg-white border-[#EEEAE0] shadow-sm"}>
              <CardHeader>
                <CardTitle className={cn("text-lg", isDark ? "text-white" : "text-[#1A1A1A]")}>Goal Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className={isDark ? "text-zinc-400" : "text-[#5F5F5F]"}>Target</span>
                  <span className={cn("font-medium", isDark ? "text-white" : "text-[#1A1A1A]")}>{goal.target_value} {goal.target_unit}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={isDark ? "text-zinc-400" : "text-[#5F5F5F]"}>Deadline</span>
                  <span className={cn("font-medium", isDark ? "text-white" : "text-[#1A1A1A]")}>{format(new Date(goal.deadline), 'MMM d, yyyy')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={isDark ? "text-zinc-400" : "text-[#5F5F5F]"}>Participants</span>
                  <span className={cn("font-medium", isDark ? "text-white" : "text-[#1A1A1A]")}>{goal.participant_count} / {goal.max_participants}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={isDark ? "text-zinc-400" : "text-[#5F5F5F]"}>Created</span>
                  <span className={cn("font-medium", isDark ? "text-white" : "text-[#1A1A1A]")}>{formatDistanceToNow(new Date(goal.created_at))} ago</span>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            {goal.status === 'ACTIVE' && (
              <Card className={isDark ? "glass-card" : "bg-white border-[#EEEAE0] shadow-sm"}>
                <CardHeader>
                  <CardTitle className={cn("text-lg", isDark ? "text-white" : "text-[#1A1A1A]")}>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    className="w-full" 
                    onClick={() => handleUpdateProgress(1)}
                    disabled={updating || userParticipation?.status !== 'ACTIVE'}
                    data-testid="quick-log-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Log +1 {goal.target_unit}
                  </Button>
                  <Button 
                    variant="outline" 
                    className={cn("w-full", isDark ? "" : "border-violet-300 text-slate-900 bg-white hover:bg-[#F4F3EE]")}
                    onClick={() => setShowShareModal(true)}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Invite Link
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Invite Code Card */}
            <Card className={isDark ? "glass-card" : "bg-white border-[#EEEAE0] shadow-sm"}>
              <CardHeader>
                <CardTitle className={cn("text-lg flex items-center gap-2", isDark ? "text-white" : "text-[#1A1A1A]")}>
                  <Share2 className="w-4 h-4 text-[#5F5F5F]" />
                  Invite Code
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <code className={cn(
                    "flex-1 p-2 rounded font-mono text-center tracking-wider",
                    isDark ? "bg-zinc-800 text-zinc-200" : "bg-[#F4F3EE] text-[#1A1A1A] border border-[#E5E2DA]"
                  )}>
                    {goal.invite_code}
                  </code>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={copyInviteLink}
                    className={isDark ? "" : "border-[#E5E2DA] text-[#5F5F5F] hover:bg-[#F4F3EE]"}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Leave/Cancel Dialog */}
      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {goal.is_creator ? 'Cancel this shared goal?' : 'Leave this shared goal?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {goal.is_creator 
                ? 'This will end the goal for all participants. This action cannot be undone.'
                : 'You will lose your progress and cannot rejoin unless invited again.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleLeaveGoal}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {goal.is_creator ? 'Cancel Goal' : 'Leave Goal'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className={cn("max-w-md", isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-[#E5E2DA]")}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-[#1A1A1A]")}>
              <Share2 className="w-5 h-5 text-[#5F5F5F]" />
              Invite Friends
            </DialogTitle>
            <DialogDescription className={isDark ? "text-zinc-400" : "text-[#5F5F5F]"}>
              Share this link to invite others to &quot;{goal.title}&quot;
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className={isDark ? "text-zinc-300" : "text-[#1A1A1A]"}>Invite Code</Label>
              <div className="flex gap-2">
                <Input 
                  value={goal.invite_code} 
                  readOnly 
                  className={cn(
                    "font-mono text-lg tracking-wider",
                    isDark ? "bg-zinc-800 border-zinc-700 text-white" : "bg-[#F4F3EE] border-[#E5E2DA] text-[#1A1A1A]"
                  )}
                />
                <Button 
                  variant="outline" 
                  onClick={copyInviteLink}
                  className={isDark ? "" : "border-[#E5E2DA] text-[#5F5F5F] hover:bg-[#F4F3EE]"}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className={isDark ? "text-zinc-300" : "text-[#1A1A1A]"}>Invite Link</Label>
              <div className="flex gap-2">
                <Input 
                  value={`${window.location.origin}/join/${goal.invite_code}`} 
                  readOnly 
                  className={cn(
                    "text-sm",
                    isDark ? "bg-zinc-800 border-zinc-700 text-white" : "bg-[#F4F3EE] border-[#E5E2DA] text-[#1A1A1A]"
                  )}
                />
                <Button 
                  variant="outline" 
                  onClick={copyInviteLink}
                  className={isDark ? "" : "border-[#E5E2DA] text-[#5F5F5F] hover:bg-[#F4F3EE]"}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <p className={cn("text-sm", isDark ? "text-zinc-500" : "text-[#5F5F5F]")}>
              {goal.participant_count} / {goal.max_participants} spots filled
            </p>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowShareModal(false)} className="btn-glossy">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove User Dialog (Admin Only) */}
      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove participant?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{userToRemove?.user_name}</strong> from this goal? 
              They will lose their progress and will need a new invite to rejoin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUserToRemove(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRemoveUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
